'use client';

import { useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  IconPlayerPlay,
  IconCopy,
  IconExternalLink,
  IconChevronLeft,
  IconChevronRight,
  IconLoader2,
} from '@tabler/icons-react';
import { toast } from 'sonner';
import type { SourceRunStatus } from '@/lib/sources/source-types';
import type { NavigationContext } from '@/lib/navigation-types';
import { formatRelativeTime } from '@/lib/projects/project-utils';
import { formatExactDate } from '@/lib/sources/source-utils';
import { StatusBadge } from '@/components/sources/StatusBadge';
import { useSheetKeyboardNav } from '@/hooks/useSheetKeyboardNav';

interface SourceDetailSheetProps {
  source: SourceRunStatus | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRunSource: (sourceId: string) => void;
  isRunning?: boolean;
  context?: NavigationContext;
}

export function SourceDetailSheet({
  source,
  open,
  onOpenChange,
  onRunSource,
  isRunning,
  context,
}: SourceDetailSheetProps) {
  const canGoPrev = context && context.currentIndex > 0;
  const canGoNext =
    context &&
    context.currentIndex >= 0 &&
    context.currentIndex < context.orderedIds.length - 1;

  const handlePrev = useCallback(() => {
    if (canGoPrev && context) context.onNavigate(context.currentIndex - 1);
  }, [canGoPrev, context]);

  const handleNext = useCallback(() => {
    if (canGoNext && context) context.onNavigate(context.currentIndex + 1);
  }, [canGoNext, context]);

  useSheetKeyboardNav(open, handlePrev, handleNext);

  const handleCopyDomain = async () => {
    if (!source) return;
    try {
      await navigator.clipboard.writeText(source.domain);
      toast.success('Domain copied');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleCopyLastRunSummary = async () => {
    if (!source) return;
    const summary = `${source.name} (${source.domain}) — Status: ${source.status}${source.message ? ` — ${source.message}` : ''} — Last run: ${formatExactDate(source.lastRunAt)}`;
    try {
      await navigator.clipboard.writeText(summary);
      toast.success('Summary copied');
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto w-[420px] md:w-[480px] sm:max-w-[480px]! p-0">
        {source && (
          <div className="flex flex-col h-full">
            {/* Header */}
            <SheetHeader className="px-6 pt-5 pb-0 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <SheetTitle className="text-lg font-semibold leading-tight">
                    <TooltipProvider delayDuration={300}>
                      <div className="flex items-center gap-0.5 shrink-0">
                        {context && context.currentIndex >= 0 && (
                          <>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 cursor-pointer"
                                  disabled={!canGoPrev}
                                  onClick={handlePrev}
                                  aria-label="Previous source"
                                >
                                  <IconChevronLeft className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Previous source (↑)</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 cursor-pointer"
                                  disabled={!canGoNext}
                                  onClick={handleNext}
                                  aria-label="Next source"
                                >
                                  <IconChevronRight className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Next source (↓)</TooltipContent>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </TooltipProvider>
                    {source.name}
                  </SheetTitle>
                  <SheetDescription className="text-sm text-muted-foreground mt-1">
                    {source.domain}
                  </SheetDescription>
                </div>
              </div>

              {/* Badge row */}
              <div className="flex flex-wrap gap-2 items-center">
                {isRunning ? (
                  <Badge variant="outline" className="border-blue-500/50 text-blue-600">
                    <IconLoader2 className="size-3 mr-1 animate-spin" />
                    Running…
                  </Badge>
                ) : (
                  <StatusBadge status={source.status} />
                )}
              </div>
            </SheetHeader>

            {/* Actions */}
            <div className="px-6 pt-4 pb-1">
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={isRunning}
                  onClick={() => onRunSource(source.sourceId)}
                  className="cursor-pointer"
                >
                  {isRunning ? (
                    <IconLoader2 className="size-4 mr-1.5 animate-spin" />
                  ) : (
                    <IconPlayerPlay className="size-4 mr-1.5" />
                  )}
                  {isRunning ? 'Running…' : 'Run this source'}
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a
                    href={`https://${source.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IconExternalLink className="size-4 mr-1.5" />
                    Open source
                  </a>
                </Button>
              </div>
            </div>

            <Separator className="my-4 mx-6 w-auto" />

            {/* Details */}
            <div className="px-6 space-y-6 flex-1">
              {/* Last run info */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold tracking-tight">Last Run Details</h3>
                <div className="space-y-1.5">
                  <div className="grid grid-cols-[100px_1fr] gap-2 text-sm">
                    <span className="text-muted-foreground">Last run</span>
                    <span>{formatExactDate(source.lastRunAt)}</span>
                  </div>
                  <div className="grid grid-cols-[100px_1fr] gap-2 text-sm">
                    <span className="text-muted-foreground">Relative</span>
                    <span>{formatRelativeTime(source.lastRunAt)}</span>
                  </div>
                  {source.message && (
                    <div className="grid grid-cols-[100px_1fr] gap-2 text-sm">
                      <span className="text-muted-foreground">Message</span>
                      <span className="text-muted-foreground">{source.message}</span>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Copy actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyDomain}
                  className="cursor-pointer"
                >
                  <IconCopy className="size-3.5 mr-1.5" />
                  Copy domain
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLastRunSummary}
                  className="cursor-pointer"
                >
                  <IconCopy className="size-3.5 mr-1.5" />
                  Copy last run summary
                </Button>
              </div>

              <Separator />

              {/* Recent runs placeholder */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold tracking-tight">Recent Runs</h3>
                <p className="text-xs text-muted-foreground">
                  Run history will be available once the backend is connected.
                </p>
              </div>
            </div>

            <div className="h-6" />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
